import { useEffect, useState } from "react";
import { db } from "../../firebase/config";
import { onValue, ref, remove, child } from "firebase/database";
import VehicleCard from "../../components/VehicleCard";
import styled from "styled-components";
import Filters from "../../components/Filters";
import Hero from "../../components/CookiePolicy";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/free-solid-svg-icons";
// Layout Wrapper
const Layout = styled.div`
  display: flex;
  padding: 20px 40px;
  gap: 15px;
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 10px;

    .NoVis{
      display:none;
    }
  }
`;

// Filter Sidebar for Mobile
interface SidebarProps {
  visible: boolean;
}
const Sidebar = styled.div<SidebarProps>`
  position: fixed;
  top: 0;
  left: ${({ visible }) => (visible ? '0' : '-600px')};
  width: 100%;
  height: 100%;
  background:white;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.3);
  padding: 20px;
  transition: left 0.3s ease;
  z-index: 100000;
  overflow-y: auto; /* Enables vertical scrolling */
  -webkit-overflow-scrolling: touch; /* Smooth scroll on iOS */
overflow-x:hidden;
  @media (min-width: 769px) {
    display: none;
  }

  /* Optional: Custom scrollbar styling (Webkit-based browsers) */
  &::-webkit-scrollbar {
    width: 0px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-track {
    background-color: #f2f2f2;
  }
`;


// Filter Toggle Button (for Mobile)
const MobileFilterToggle = styled.button`
  display: none;
  top: 20px;
  left: 20px;
  background: #e61717;
  margin-top:10px;
  color: white;
  padding: 10px 20px;
  font-family:var(--font-medium);
  border: none;
  width:fit-content;
  margin-left:20px;
  border-radius: 8px;
  cursor: pointer;
gap:10px;
  @media (max-width: 768px) {
    display: block;
  }
`;

// Close Button inside Sidebar
const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 40px;
  color:#e61717;
  cursor: pointer;
  position: absolute;
  right: 37px;
  top: 30px;
`;

// Spinner (Loading Indicator)
const Spinner = styled.div`
  border: 8px solid #f3f3f3;
  border-top: 6px solid #e61717; /* Customize color */
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Wrapper for Loading State
const LoadingWrapper = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  font-size: 30px;
  color: #555;
  padding: 100px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

// Message for No Results
const Message = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  font-size: 30px;
  color: #555;
  padding: 100px 0;
`;

// Grid for Vehicle Cards
const Grid = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  padding: 0 20px;
`;


const Home = () => {
  const [filters, setFilters] = useState<any>({});
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [makeOptions, setMakeOptions] = useState<{ label: string; value: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSidebar, setShowSidebar] = useState(false);

  useEffect(() => {
    setLoading(true);

    const vehicleRef = ref(db, "vehicles");
    onValue(vehicleRef, (snapshot) => {
      const data = snapshot.val();
      let loadedVehicles = [];

      if (data) {
        loadedVehicles = Object.entries(data).map(([key, value]: [string, any]) => ({
          id: key, // Add ID for delete reference
          ...value,
        }));
      }

      const makes = Array.from(new Set(loadedVehicles.map((v: any) => v.make))).filter(Boolean);
      setMakeOptions(makes.map((m) => ({ label: m, value: m })));

      // Filters
      if (filters.make && filters.make.length > 0) {
        loadedVehicles = loadedVehicles.filter((v: any) => filters.make.includes(v.make));
      }

      if (filters.model && filters.model.length > 0) {
        loadedVehicles = loadedVehicles.filter((v: any) => filters.model.includes(v.model));
      }

      if (filters.sort) {
        switch (filters.sort) {
          case "price-asc":
            loadedVehicles.sort((a: any, b: any) => a.price - b.price);
            break;
          case "price-desc":
            loadedVehicles.sort((a: any, b: any) => b.price - a.price);
            break;
          case "km-asc":
            loadedVehicles.sort((a: any, b: any) => a.km - b.km);
            break;
          case "km-desc":
            loadedVehicles.sort((a: any, b: any) => b.km - a.km);
            break;
          case "year-asc":
            loadedVehicles.sort((a: any, b: any) => a.year - b.year);
            break;
          case "year-desc":
            loadedVehicles.sort((a: any, b: any) => b.year - a.year);
            break;
          case "make-asc":
            loadedVehicles.sort((a: any, b: any) => a.make.localeCompare(b.make));
            break;
          case "make-desc":
            loadedVehicles.sort((a: any, b: any) => b.make.localeCompare(a.make));
            break;
        }
      }

      if (filters.trim && filters.trim.length > 0) {
        loadedVehicles = loadedVehicles.filter((v: any) => filters.trim.includes(v.trim));
      }

      if (filters.yearMin || filters.yearMax) {
        loadedVehicles = loadedVehicles.filter(
          (v: any) =>
            (!filters.yearMin || v.year >= parseInt(filters.yearMin)) &&
            (!filters.yearMax || v.year <= parseInt(filters.yearMax))
        );
      }

      if (filters.kmmin || filters.kmmax) {
        loadedVehicles = loadedVehicles.filter(
          (v: any) =>
            (!filters.kmmin || v.km >= parseInt(filters.kmmin)) &&
            (!filters.kmmax || v.km <= parseInt(filters.kmmax))
        );
      }

      if (filters.color && filters.color.length > 0) {
        loadedVehicles = loadedVehicles.filter((v: any) => filters.color.includes(v.extColor));
      }

      if (filters.engine && filters.engine.length > 0) {
        loadedVehicles = loadedVehicles.filter((v: any) => filters.engine.includes(v.engine));
      }

      if (filters.transmission && filters.transmission.length > 0) {
        loadedVehicles = loadedVehicles.filter((v: any) =>
          filters.transmission.includes(v.transmission)
        );
      }

      if (filters.drivetrain && filters.drivetrain.length > 0) {
        loadedVehicles = loadedVehicles.filter((v: any) =>
          filters.drivetrain.includes(v.drivetrain)
        );
      }

      setVehicles(loadedVehicles);
      setLoading(false);
    });
  }, [filters]);

  // Delete function
  const handleDelete = (vehicleId: string) => {
    const vehicleRef = ref(db, `vehicles/${vehicleId}`);
    remove(vehicleRef)
      .then(() => {
        setVehicles((prev) => prev.filter((v) => v.id !== vehicleId));
      }
      )
      .catch((error) => {
        console.error("Error deleting vehicle:", error);
      });
      alert("Removed Successfully!")
  };

  // Model options by make
  const modelOptions = async (make: string) => {
    const vehicleRef = ref(db, "vehicles");
    return new Promise<{ label: string; value: string }[]>((resolve) => {
      onValue(
        vehicleRef,
        (snapshot) => {
          const data = snapshot.val();
          const models = Array.from(
            new Set(
              Object.values(data || {})
                .filter((v: any) => v.make === make)
                .map((v: any) => v.model)
            )
          ).filter(Boolean);
          resolve(models.map((m) => ({ label: m, value: m })));
        },
        { onlyOnce: true }
      );
    });
  };

  // Trim options by make & model
  const trimOptions = async (make: string, model: string) => {
    const vehicleRef = ref(db, "vehicles");
    return new Promise<{ label: string; value: string }[]>((resolve) => {
      onValue(
        vehicleRef,
        (snapshot) => {
          const data = snapshot.val();
          const trims = Array.from(
            new Set(
              Object.values(data || {})
                .filter((v: any) => v.make === make && v.model === model)
                .map((v: any) => v.trim)
            )
          ).filter(Boolean);
          resolve(trims.map((t) => ({ label: t, value: t })));
        },
        { onlyOnce: true }
      );
    });
  };

  return (
    <>
   <Hero />
<Layout>
  <MobileFilterToggle onClick={() => setShowSidebar(true)}><FontAwesomeIcon style={{marginRight:5}} icon={faFilter}/>Filters</MobileFilterToggle>
  
  <Sidebar visible={showSidebar}>
    <CloseButton onClick={() => setShowSidebar(false)}>×</CloseButton>
    <Filters
      onFilterChange={setFilters}
      makeOptions={makeOptions}
      modelOptions={modelOptions}
      trimOptions={trimOptions}
    />
  </Sidebar>

<div className="NoVis">
  <Filters
      onFilterChange={setFilters}
      makeOptions={makeOptions}
      modelOptions={modelOptions}
      trimOptions={trimOptions}
    /></div>
  <Grid>
    {loading ? (
      <LoadingWrapper>
        <Spinner />
        Loading...
      </LoadingWrapper>
    ) : vehicles.length === 0 ? (
      <Message>Sorry, your current filters returned no result!</Message>
    ) : (
      vehicles.map((vehicle, i) => (
        <VehicleCard key={i} {...vehicle} onDelete={handleDelete} />
      ))
    )}
  </Grid>
</Layout>

    </>
  );
};

export default Home;
